import type { Service } from '@/services/firestore/types'
import { catalogPrice } from '@/utils/catalogDisplay'
import { MenuEyebrow } from '@/components/printMenu/MenuEyebrow'
import { MenuPageShell } from '@/components/printMenu/MenuPageShell'
import { MenuServiceEditorial } from '@/components/printMenu/MenuServiceEditorial'
import { MenuServiceImage } from '@/components/printMenu/MenuServiceImage'
import { MenuServiceMetaRow } from '@/components/printMenu/MenuServiceMetaRow'
import { cn } from '@/lib/utils'

type StandardServicePageProps = {
  sectionEyebrow: string
  service: Service
  imageSrc: string
  imageAlt: string
  pageNumber: number
  totalPages: number
}

function StandardServiceCopy({
  service,
  className,
}: {
  service: Service
  className?: string
}) {
  return (
    <div className={cn('flex flex-col', className)}>
      <header>
        <h2 className="font-display text-[length:var(--menu-fs-title-lg)] font-medium leading-[1.12] tracking-[-0.02em] text-ink">
          {service.name}
        </h2>
        <MenuServiceMetaRow
          durationMin={service.durationMinutes}
          price={catalogPrice(service)}
          mostPopular={service.mostPopular}
          className="mt-4"
        />
      </header>

      <p className="mt-6 font-sans text-[length:var(--menu-fs-base)] leading-[1.65] text-body">
        {service.description}
      </p>

      <MenuServiceEditorial
        chooseWhen={service.chooseWhen ?? []}
        highlights={service.highlights}
        className="mt-5"
      />
    </div>
  )
}

export function StandardServicePage({
  sectionEyebrow,
  service,
  imageSrc,
  imageAlt,
  pageNumber,
  totalPages,
}: StandardServicePageProps) {
  const stacked = service.printImageLayout === 'stacked'

  return (
    <MenuPageShell pageNumber={pageNumber} totalPages={totalPages}>
      <MenuEyebrow>{sectionEyebrow}</MenuEyebrow>

      {stacked ? (
        <div className="mt-5 flex min-h-0 flex-1 flex-col overflow-hidden">
          <MenuServiceImage src={imageSrc} alt={imageAlt} layout="landscape" className="shrink-0" />
          <StandardServiceCopy service={service} className="mt-5 min-h-0 flex-1 overflow-hidden" />
        </div>
      ) : (
        <div className="mt-8 grid min-h-0 flex-1 grid-cols-[1fr_1.15fr] gap-[10mm] overflow-hidden">
          <MenuServiceImage src={imageSrc} alt={imageAlt} />
          <StandardServiceCopy service={service} className="pt-2" />
        </div>
      )}
    </MenuPageShell>
  )
}
